import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Clock, User, Tag, Filter, RefreshCw } from "lucide-react";

const ACTION_LABELS = {
  create: "Erstellt",
  update: "Aktualisiert",
  delete: "Gelöscht",
  status_change: "Status geändert",
  login: "Angemeldet",
};

const RESOURCE_LABELS = {
  customer: "Kunde",
  ticket: "Ticket",
  password: "Passwort",
  document: "Dokument",
  hardware: "Hardware",
  network: "Netzwerk",
  user: "Benutzer",
  file: "Datei",
};

const ACTION_COLORS = {
  create: "status-done",
  update: "status-in-progress",
  delete: "text-destructive bg-destructive/10",
  status_change: "status-in-progress",
  login: "status-open",
};

function timeAgo(isoStr) {
  if (!isoStr) return "—";
  const diff = Date.now() - new Date(isoStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return `vor ${d} Tag(en)`;
}

export default function ActivityPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter) params.resource_type = filter;
      const r = await api.get("/activity", { params });
      setLogs(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = userFilter ? logs.filter(l => l.user_name === userFilter) : logs;
  const uniqueUsers = [...new Set(logs.map(l => l.user_name).filter(Boolean))];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Aktivitäts-Log</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} Einträge — Wer hat was wann geändert</p>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="activity-resource-filter"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Typen</option>
            {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            data-testid="activity-user-filter"
            value={userFilter}
            onChange={e => setUserFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
          >
            <option value="">Alle Benutzer</option>
            {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <button
            data-testid="activity-refresh"
            onClick={load}
            className="p-2 bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            title="Aktualisieren"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="bg-card border border-border" data-testid="activity-log-table">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Laden...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Keine Aktivitäten gefunden</div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((log, i) => (
              <div key={log._id || i} className="flex items-start gap-4 px-5 py-3 hover:bg-muted/20 transition-colors" data-testid={`activity-row-${i}`}>
                {/* User avatar */}
                <div className="initials-avatar flex-shrink-0 mt-0.5">
                  {log.user_initials || "?"}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{log.user_name || "System"}</span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 ${ACTION_COLORS[log.action] || "status-open"}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                    {log.resource_type && (
                      <span className="text-xs bg-secondary text-secondary-foreground px-1.5 py-0.5 font-medium">
                        {RESOURCE_LABELS[log.resource_type] || log.resource_type}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 truncate">
                    {typeof log.details === "string"
                      ? log.details
                      : log.details && typeof log.details === "object"
                        ? (log.details.title || log.details.name || Object.values(log.details).filter(v => typeof v === "string").join(" · "))
                        : ""}
                  </p>
                </div>

                {/* Time */}
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-muted-foreground">{timeAgo(log.timestamp)}</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
